<template>
  <div class="bp-data-table">
    <div v-if="loading" class="text-center py-9">
      <b-spinner variant="primary" />
    </div>

    <div v-else-if="!data.length" class="mt-9">
      <slot name="empty">
        <div class="row justify-content-center text-center">
          <div class="col-lg-5">
            <slot name="no-content">
              <img
                class="d-block mb-5 mx-auto"
                src="@/assets/images/no-content.svg"
              />

              <h4 v-if="!noMessage">Ainda não há nada aqui</h4>
              <div
                class="functional-1 text-neutral-300 mt-3"
                v-if="emptyMessage"
              >
                {{ emptyMessage }}
              </div>

              <div
                v-if="$slots['empty-button']"
                class="mt-6 d-flex flex-column flex-lg-row justify-content-center"
              >
                <slot name="empty-button" />
              </div>
            </slot>
          </div>
        </div>
      </slot>
    </div>

    <template v-else>
      <div v-if="isDesktop" class="bp-desktop-table" :class="tableClasses">
        <div :style="tableStyle" class="bp-desktop-table">
          <div v-for="(column, index) in columns" :key="column.name">
            <slot :name="'header-' + getSlotName(column, index)">
              <div class="p-3 text-neutral-500">
                <h4>
                  {{ column.name }}
                </h4>
              </div>
            </slot>
          </div>
        </div>

        <div
          v-for="(item, i) in data"
          class="bp-desktop-table border-bottom border-neutral-076"
          :key="i"
          :style="tableStyle"
        >
          <div v-for="(column, cidx) in columns" :key="column.name" class="p-3">
            <bp-data-table-dropdown
              v-if="column.type == 'menu'"
              :item="item"
              :options="getOptions(item, column)"
              :disabled="isDisabled(item, column)"
              @click="clickOption"
            />
            <slot
              v-else
              :item="item"
              :value="getFieldValue(item, column)"
              :name="getSlotName(column, cidx)"
            >
              <bp-data-table-field :item="item" :column="column" />
            </slot>
          </div>
        </div>
      </div>

      <div v-else class="bp-responsive-table">
        <bp-select
          v-model="selectedCategory"
          size="md"
          label="Visualizar categoria"
          :options="categories"
        />

        <div
          v-for="(item, i) in data"
          class="border-neutral-076"
          :key="i"
          :class="currentIndex !== i && 'border-bottom'"
        >
          <div
            class="bp-responsive-table-data-header py-5"
            @click="toggleRow(i)"
          >
            <slot
              :item="item"
              :name="getSlotName(mainColumn)"
              :value="getFieldValue(item, mainColumn)"
            >
              <bp-data-table-field :column="mainColumn" :item="item" />
            </slot>

            <slot
              :item="item"
              :value="getFieldValue(item, activeColumn)"
              :name="getSlotName(activeColumn)"
            >
              <bp-data-table-field :item="item" :column="activeColumn" />
            </slot>

            <bp-icon
              :icon="currentIndex === i ? 'fa fa-angle-up' : 'fa fa-angle-down'"
            />
          </div>

          <template v-if="currentIndex === i">
            <div class="d-flex flex-column gap-3 my-5">
              <div
                class="d-flex justify-content-between align-items-start gap-5"
                v-for="(column, cidx) in columns.filter(c => c.type !== 'menu')"
                :key="column.name || cidx"
              >
                <slot :name="'header-' + getSlotName(column, cidx)">
                  <div class="text-neutral-500">
                    <h4>
                      {{ column.name }}
                    </h4>
                  </div>
                </slot>

                <div class="text-right">
                  <slot
                    :item="item"
                    :value="getFieldValue(item, column)"
                    :name="getSlotName(column, cidx)"
                  >
                    <bp-data-table-field :item="item" :column="column" />
                  </slot>
                </div>
              </div>
            </div>

            <div v-if="optionsColumn" class="d-flex flex-column">
              <bp-button
                v-for="(option, index) in getOptions(item, optionsColumn)"
                class="mb-3"
                variant="filled-1"
                :key="index"
                :icon="option.icon"
                :label="option.label"
                :class="option.class"
                :color="option.color || 'neutral'"
                :disabled="isDisabled(item, optionsColumn)"
                @click="clickOption(option, item)"
              />
            </div>
          </template>
        </div>
      </div>
    </template>

    <bp-dialog
      v-model="dialog"
      v-bind="dialogProps"
      @action="onDialogAction"
      @secondary-action="onDialogSecondaryAction"
    />
  </div>
</template>

<script>
import isMobile from "ismobilejs";
import BpDataTableDropdown from "./BpDataTableDropdown.vue";
import BpDataTableField from "./BpDataTableField.vue";
export default {
  components: { BpDataTableDropdown, BpDataTableField },
  props: {
    data: {
      type: Array,
      default: () => []
    },
    emptyMessage: {
      type: String,
      default: ""
    },
    columns: {
      type: Array,
      default: () => []
    },
    main: {
      type: String,
      default: ""
    },
    loading: {
      type: Boolean,
      default: false
    },
    noMessage: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      selectedCategory: this.columns[1]?.name || "",
      categories: this.columns
        .filter(c => !!c.name)
        .map(col => ({ id: col.name, name: col.name })),
      currentIndex: undefined,

      dialog: false,
      dialogProps: {},
      dialogItem: undefined,
      dialogAction: undefined,
      dialogSecondaryAction: undefined
    };
  },
  computed: {
    isDesktop() {
      return !isMobile().any;
    },
    tableClasses() {
      const classes = [];

      return classes;
    },
    tableStyle() {
      const columnSizes = this.isDesktop
        ? this.columns
            .map(c => (c.type == "menu" ? "50px" : c.size || "1fr"))
            .join(" ")
        : "1fr auto auto";

      return {
        display: "grid",
        gridTemplateColumns: columnSizes
      };
    },
    optionsColumn() {
      return this.columns.find(c => c.type == "menu");
    },
    activeColumn() {
      return this.columns.find(c => c.name == this.selectedCategory);
    },
    mainColumn() {
      if (!this.main) return this.columns[0];
      return this.columns.find(
        c => c.name.toLowerCase() === this.main.toLowerCase()
      );
    }
  },
  methods: {
    getSlotName(column, index) {
      if (column.name)
        return column.name
          .toLowerCase()
          .split(" ")
          .join("-");
      return index + 1;
    },
    getFieldValue(item, column) {
      const value = item[column.field];

      if (column.transform) return column.transform(value, item);

      return item[column.field] ?? column.default ?? "-";
    },
    isDisabled(item, column) {
      if (typeof column.disabled == "function") return column.disabled(item);

      return column.disabled;
    },
    getOptions(item, column) {
      if (typeof column.options == "function") return column.options(item);

      return column.options || [];
    },
    toggleRow(index) {
      if (this.currentIndex === index) this.currentIndex = undefined;
      else this.currentIndex = index;
    },

    clickOption(option, item) {
      if (option.dialog) {
        const dialog = option.dialog;
        if (dialog.action) this.dialogAction = dialog.action;
        if (dialog.secondaryAction)
          this.dialogSecondaryAction = dialog.secondaryAction;

        delete dialog.action;
        delete dialog.secondaryAction;

        this.dialogProps = { ...dialog };
        this.dialogItem = item;

        this.dialog = true;
      } else return option.click(item);
    },

    onDialogAction() {
      if (this.dialogAction) this.dialogAction(this.dialogItem);
      this.dialog = false;
    },
    onDialogSecondaryAction() {
      if (this.dialogSecondaryAction)
        this.dialogSecondaryAction(this.dialogItem);

      this.dialog = false;
    },
    clearDialog() {
      this.dialogItem = undefined;
      this.dialogAction = undefined;
      this.dialogSecondaryAction = undefined;
      this.dialogProps = {};
    }
  },
  watch: {
    dialog(v) {
      if (!v) setTimeout(this.clearDialog, 300);
    },
    loading() {}
  }
};
</script>

<style lang="scss" scoped>
.bp-data-table {
  .blur {
    filter: blur(6px);
    user-select: none;
  }
  .bp-desktop-table {
    display: grid;
    align-items: center;

    > div {
      overflow: hidden;
    }
  }

  .bp-responsive-table {
    gap: 16px;
    display: flex;
    flex-direction: column;

    .bp-responsive-table-data-header {
      display: grid;
      grid-template-columns: 1fr auto auto;
      align-items: start;
      gap: 16px;
    }
  }
}
</style>
